// Which pages carry an Article-family node in app.vue's sitewide @graph, and which type.
// Site Audit and AI answer engines read headline, author, datePublished and dateModified
// from that node to judge how fresh and attributable a page is. Pages that emit their own
// Article/BlogPosting (blog posts, learn guides, research, alternatives, release notes)
// keep theirs and the sitewide one is dropped (utils/single-page-schema.ts).
// tests/seo/article-schema.test.ts and scripts/validate-unique-schema.mjs enforce it.

export const ARTICLE_TYPES = ['Article', 'BlogPosting', 'NewsArticle', 'TechArticle'] as const;
export type ArticleType = (typeof ARTICLE_TYPES)[number];

// Legal documents (English only, generated from legal/src, outside seo-routes).
export const legalArticlePaths = new Set([
  '/acceptable-use',
  '/accessibility',
  '/ai',
  '/delete-account',
  '/dmca',
  '/dpa',
  '/legal',
  '/privacy-policy',
  '/refund-policy',
  '/security',
  '/sms-terms',
  '/sub-processors',
  '/terms-of-service',
]);

// The home page and /pricing are the product's storefront (SoftwareApplication, OfferCatalog),
// and /contact-sales is a form: none of them is an article.
export const nonArticlePaths = new Set(['/', '/pricing', '/contact-sales']);

// Pages that document how the product itself works (what a feature does, how an integration
// connects, how security and the roadmap stand, step-by-step video tutorials) are TechArticle.
// Everything else that is written content (comparisons, guides, solutions, tools,
// templates, hubs, company pages) is a plain Article.
const techArticleSections = new Set([
  'features',
  'integrations',
  'tutorials',
  'security',
  'trust',
  'roadmap',
  'workspace369-features',
  'help-center',
  'support',
  'app',
  'ai',
  'accessibility',
  'sub-processors',
]);

export const articleTypeForPath = (path: string): ArticleType | null => {
  if (nonArticlePaths.has(path)) return null;
  const section = path.split('/').filter(Boolean)[0] || '';
  return techArticleSections.has(section) ? 'TechArticle' : 'Article';
};

export const isArticleType = (type: unknown) =>
  [type].flat().some((item) => (ARTICLE_TYPES as readonly unknown[]).includes(item));
